import { GridColDef, GridSortCellParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { ICountyOptions } from '@modules/admin/Tools/LenderUtility/interfaces';
import { getOptions, newGridRecordId } from '@shared/commonFunctions';
import { dataGridCol, gridCurrencyCell, gridStateCell } from '@shared/components/grid/common';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { Wrapper } from '@styles/CommonStyles';

import { requestAddNewTitleFee, requestDeleteTitleFee, requestUpdateTitleFee } from './queries';
import { TitleFeesValidationSchema } from './validation';

export interface ITitileFees {
  id: string;
  state: string;
  countyIds: string[];
  straightRefi: number;
  add: number;
  drop: number;
  duplicateTitleFee: number;
  outOfState: number;
  category: string;
  description: string;
}

const getCountyString = (countyList: ICountyOptions[]) =>
  (countyList || []).map((value: ICountyOptions) => value.countyName).join(', ') || 'All';

export const TitleFees = () => {
  const apiRef = useGridApiRef();

  const { data: countyData } = useSWR<ICountyOptions[]>(`${SERVICES.LENDER}/lenders/county`);
  const { data: titleFeesData = [], isValidating, error } = useSWR<ITitileFees[]>(`${SERVICES.LENDER}/titlefees`);

  const [loading, setLoading] = useState(false);

  const isLoading = useMemo(() => (!error && !titleFeesData) || isValidating || loading, [loading, isValidating, error, titleFeesData]);

  const getCountyOptions = useCallback(
    (selectedState: string) => {
      const stateCountyOptions = countyData
        ?.filter((value) => value.countyState === selectedState)
        ?.map((value) => ({ ...value, label: `${value.countyState}-${value.countyName}` }));

      return stateCountyOptions || [];
    },
    [countyData]
  );

  const columns: GridColDef[] = useMemo(
    () =>
      [
        dataGridCol('state', { ...gridStateCell, minWidth: 40 }),
        dataGridCol('countyIds', {
          headerName: 'County Name',
          flex: 3,
          textWrap: true,
          renderEditCell: (params) => {
            const options = getCountyOptions(params.row.state);

            return (
              <GridColSelect
                isSearchable
                multiple
                limitTags={1}
                placeholder={params?.value?.length ? '' : 'All'}
                options={getOptions(options, 'id', 'label')}
                params={params}
                placeholderOpacity={1}
              />
            );
          },
          valueFormatter: (params) => (params.id ? getCountyString(apiRef.current.getRow(params.id).countyList) : ''),
          sortComparator: (v1: string[], v2: string[], param1: GridSortCellParams, param2: GridSortCellParams) => {
            const value1 = getCountyString(apiRef.current.getRow(param1.id).countyList);
            const value2 = getCountyString(apiRef.current.getRow(param2.id).countyList);
            return value1.localeCompare(value2);
          },
          editable: true,
        }),
        dataGridCol('straightRefi', {
          ...gridCurrencyCell,
          renderEditCell: (params) => (
            <GridColNumber
              params={params}
              prefix="$"
              decimalScale={2}
            />
          ),
          flex: 1,
          editable: true,
        }),
        dataGridCol('add', {
          ...gridCurrencyCell,
          renderEditCell: (params) => (
            <GridColNumber
              params={params}
              prefix="$"
              decimalScale={2}
            />
          ),
          flex: 1,
          editable: true,
        }),
        dataGridCol('drop', {
          ...gridCurrencyCell,
          renderEditCell: (params) => (
            <GridColNumber
              params={params}
              prefix="$"
              decimalScale={2}
            />
          ),
          flex: 1,
          editable: true,
        }),
        dataGridCol('duplicateTitleFee', {
          ...gridCurrencyCell,
          textWrap: true,
          renderEditCell: (params) => (
            <GridColNumber
              params={params}
              prefix="$"
              decimalScale={2}
            />
          ),
          flex: 1,
          editable: true,
        }),
        dataGridCol('outOfState', {
          ...gridCurrencyCell,
          renderEditCell: (params) => (
            <GridColNumber
              params={params}
              prefix="$"
              decimalScale={2}
            />
          ),
          flex: 1,
          editable: true,
        }),
        dataGridCol('category', {
          renderEditCell: (params) => <GridColTextInput params={params} />,
          flex: 1,
          editable: true,
        }),
        dataGridCol('description', {
          renderEditCell: (params) => <GridColTextInput params={params} />,
          flex: 2,
          editable: true,
        }),
      ].map((column) => ({ ...column })),
    [countyData]
  );

  const gridActions = {
    onSave: async (row: GridValidRowModel) => {
      const isStateMatched = getCountyOptions(row.state).find(
        (value) => row.countyIds?.find((county: string) => county === value.id)
      );
      setLoading(true);
      row?.id === newGridRecordId
        ? await requestAddNewTitleFee({ ...row, countyIds: isStateMatched ? row.countyIds : [] } as ITitileFees)
        : await requestUpdateTitleFee(row.id, { ...row, countyIds: isStateMatched ? row.countyIds : [] } as ITitileFees);
      setLoading(false);
    },
    onDelete: (row: GridValidRowModel) => requestDeleteTitleFee(row.id),
  };

  return (
    <Wrapper>
      <PhoenixGrid
        autoHeight
        autoRowHeight
        withHeader
        isFlexible
        showClearAllFilter
        disableSelectionOnClick
        apiRef={apiRef}
        name="Title Fee"
        columns={columns}
        loading={isLoading}
        rows={isLoading ? [] : titleFeesData}
        validateSchema={TitleFeesValidationSchema}
        actions={gridActions}
      />
    </Wrapper>
  );
};
